// src/pages/masterdata/item/ItemList.jsx
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  ChevronRight, 
  ChevronDown, 
  FolderOpen, 
  Folder,
  Package,
  Edit,
  Trash2,
  Eye,
  RefreshCw,
  Download,
  Upload,
  AlertTriangle,
  X as CloseIcon
} from 'lucide-react';
import toast from 'react-hot-toast';
import ItemForm from './ItemForm';
import itemService from '../../../services/itemService';

const ItemList = () => {
  // State management
  const [items, setItems] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [selectedItem, setSelectedItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterGroup, setFilterGroup] = useState('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Fetch data on mount
  useEffect(() => {
    fetchItems();
    fetchStatistics();
  }, []);

  // Fetch items tree
  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const data = await itemService.getItemTree();
      setItems(data);
      
      // Auto-expand root items
      const rootIds = data.map(item => item.id.toString());
      setExpandedItems(new Set(rootIds));
    } catch (error) {
      console.error('Error fetching items:', error);
      // Error already handled by axios interceptor
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const stats = await itemService.getItemStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  // Toggle expand/collapse
  const toggleExpand = (itemId) => {
    const newExpanded = new Set(expandedItems);
    const idStr = itemId.toString();
    
    if (newExpanded.has(idStr)) {
      newExpanded.delete(idStr);
    } else {
      newExpanded.add(idStr);
    }
    setExpandedItems(newExpanded);
  };

  // CRUD Handlers
  const handleCreate = (parentItem = null) => {
    setSelectedItem(parentItem);
    setFormMode('create');
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormMode('edit');
    setShowForm(true);
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setFormMode('view');
    setShowForm(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    try {
      await itemService.softDeleteItem(itemToDelete.id);
      toast.success('Item deleted successfully!');
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      fetchItems(); // Refresh list
      fetchStatistics(); // Refresh stats
    } catch (error) {
      console.error('Error deleting item:', error);
      // Error already handled by axios interceptor
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedItem(null);
  };

  const handleFormSave = () => {
    handleFormClose();
    fetchItems(); // Refresh list
    fetchStatistics(); // Refresh stats
  };

  const handleRefresh = () => {
    fetchItems();
    fetchStatistics();
    toast.success('Data refreshed!');
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Filter items based on search and filters
  const filterItems = (itemsList) => {
    return itemsList.filter(item => {
      // Search filter
      const matchesSearch = !searchQuery || 
        item.itemCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.itemName.toLowerCase().includes(searchQuery.toLowerCase());

      // Type filter
      const matchesType = filterType === 'all' || 
        (filterType === 'material' && item.itemType === 'Material') ||
        (filterType === 'service' && item.itemType === 'Service');

      // Group filter
      const matchesGroup = filterGroup === 'all' ||
        (filterGroup === 'groups' && item.isGroup) ||
        (filterGroup === 'items' && !item.isGroup);

      return matchesSearch && matchesType && matchesGroup;
    }).map(item => ({
      ...item,
      children: item.children ? filterItems(item.children) : []
    }));
  };

  const filteredItems = filterItems(items);

  // Recursive tree renderer
  const renderTreeItem = (item, depth = 0) => {
    const isExpanded = expandedItems.has(item.id.toString());
    const hasChildren = item.children && item.children.length > 0;
    const indentWidth = depth * 24;

    return (
      <React.Fragment key={item.id}>
        <tr className={`border-b border-gray-200 hover:bg-blue-50 transition-colors ${depth === 0 ? 'bg-gray-50' : ''}`}>
          <td className="px-6 py-3">
            <div className="flex items-center gap-2" style={{ paddingLeft: `${indentWidth}px` }}>
              {hasChildren ? (
                <button
                  onClick={() => toggleExpand(item.id)}
                  className="p-1 hover:bg-blue-100 rounded transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-blue-600" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-blue-600" />
                  )}
                </button>
              ) : (
                <div className="w-6" />
              )}

              {item.isGroup ? (
                isExpanded ? (
                  <FolderOpen className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Folder className="w-5 h-5 text-yellow-600" />
                )
              ) : (
                <Package className="w-5 h-5 text-blue-600" />
              )}

              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-gray-600">
                    {item.itemCode}
                  </span>
                  <span className="font-semibold text-gray-900">
                    {item.itemName}
                  </span>
                  {item.isGroup ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                      GROUP
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      ITEM
                    </span>
                  )}
                  {!item.isActive && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                      INACTIVE
                    </span>
                  )}
                </div>
                {item.specification && (
                  <div className="text-xs text-gray-500 mt-1">
                    {item.specification}
                  </div>
                )}
              </div>
            </div>
          </td>

          <td className="px-6 py-3 whitespace-nowrap">
            {item.itemType === 'Material' ? (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                Material
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                Service
              </span>
            )}
          </td>

          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
            {item.unit || '-'}
          </td>

          <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
            {formatCurrency(item.defaultPrice)}
          </td>

          <td className="px-6 py-3 whitespace-nowrap text-right">
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => handleView(item)}
                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="View Details"
              >
                <Eye className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleEdit(item)}
                className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                title="Edit Item"
              >
                <Edit className="w-4 h-4" />
              </button>
              {item.isGroup && (
                <button
                  onClick={() => handleCreate(item)}
                  className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                  title="Add Child Item"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => handleDeleteClick(item)}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Delete Item"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </td>
        </tr>

        {hasChildren && isExpanded && item.children.map(child => 
          renderTreeItem(child, depth + 1)
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Item Master Data</h1>
            <p className="text-gray-600 mt-1">Manage budget items with hierarchical structure</p>
          </div>
          <button
            onClick={() => handleCreate(null)}
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Root Item
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by item code or name..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="material">Material Only</option>
            <option value="service">Service Only</option>
          </select>

          <select
            value={filterGroup}
            onChange={(e) => setFilterGroup(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">All Items</option>
            <option value="groups">Groups Only</option>
            <option value="items">Items Only</option>
          </select>

          <div className="flex gap-2">
            <button
              onClick={handleRefresh}
              className="border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-lg shadow-lg p-4 text-white">
            <div className="text-sm font-medium text-blue-100">Total Items</div>
            <div className="text-2xl font-bold mt-1">{statistics.totalItems}</div>
            <div className="text-xs text-blue-100 mt-1">All active items</div>
          </div>
          <div className="bg-gradient-to-br from-yellow-500 to-yellow-400 rounded-lg shadow-lg p-4 text-white">
            <div className="text-sm font-medium text-yellow-100">Groups</div>
            <div className="text-2xl font-bold mt-1">{statistics.groupCount}</div>
            <div className="text-xs text-yellow-100 mt-1">Item groups</div>
          </div>
          <div className="bg-gradient-to-br from-green-600 to-green-500 rounded-lg shadow-lg p-4 text-white">
            <div className="text-sm font-medium text-green-100">Materials</div>
            <div className="text-2xl font-bold mt-1">{statistics.materialCount}</div>
            <div className="text-xs text-green-100 mt-1">Material items</div>
          </div>
          <div className="bg-gradient-to-br from-purple-600 to-purple-500 rounded-lg shadow-lg p-4 text-white">
            <div className="text-sm font-medium text-purple-100">Services</div>
            <div className="text-2xl font-bold mt-1">{statistics.serviceCount}</div>
            <div className="text-xs text-purple-100 mt-1">Service items</div>
          </div>
        </div>
      )}

      {/* Tree Table */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gradient-to-r from-red-600 to-red-500 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  Item Code & Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  Unit
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  Default Price
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="flex space-x-2">
                        <div className="w-3 h-3 bg-red-400 rounded-full animate-bounce"></div>
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-3 h-3 bg-red-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <p className="text-gray-500 text-sm">Loading items...</p>
                    </div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-sm font-medium">No items found</p>
                    <p className="text-gray-400 text-xs mt-1">Click "Add Root Item" to create your first item</p>
                  </td>
                </tr>
              ) : (
                filteredItems.map(item => renderTreeItem(item))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg flex items-center gap-3">
              <AlertTriangle className="w-6 h-6" />
              <h3 className="text-lg font-bold">Delete Confirmation</h3>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete this item?
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                <div className="flex items-center gap-2 mb-2">
                  {itemToDelete.isGroup ? (
                    <Folder className="w-5 h-5 text-yellow-600" />
                  ) : (
                    <Package className="w-5 h-5 text-blue-600" />
                  )}
                  <span className="font-mono text-sm text-gray-600">
                    {itemToDelete.itemCode}
                  </span>
                </div>
                <div className="font-semibold text-gray-900">
                  {itemToDelete.itemName}
                </div>
              </div>
              <p className="text-red-600 text-sm mt-4 font-medium">
                ⚠️ This action cannot be undone.
              </p>
            </div>

            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex items-center justify-end gap-3 border-t border-gray-200">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Form Modal */}
      {showForm && (
        <ItemForm
          mode={formMode}
          item={selectedItem}
          onClose={handleFormClose}
          onSave={handleFormSave}
        />
      )}
    </div>
  );
};

export default ItemList;